import {
  BuildingIcon,
  ColumnDefinition,
  SearchIcon,
  SortDirection,
  ZeroState,
  ZeroStateProps,
} from "@codat/orchard-ui";
import { GetCompaniesResponse, GetCompanyResponse } from "api/public";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { formatDateAndTimeWithShortString } from "utils/formatDate";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";
import DataTable from "components/DataTable";
import Pagination from "components/Pagination";
import { EditCompanyFormState } from "pages/Companies/EditCompanyModal";

import { CompaniesSortKey } from "../../Companies";
import { CompanyCell, CompanyCellModel } from "../TableCells/CompanyCell";
import {
  ConnectionsCell,
  ConnectionsCellModel,
} from "../TableCells/ConnectionsCell";
import { CreatedCell, CreatedCellModel } from "../TableCells/CreatedCell";
import { CTACellModel, CtaCell } from "../TableCells/CTACell";
import { LastPulledCell } from "../TableCells/LastPulledCell";
import styles from "./CompaniesTable.module.scss";

type CompanyTableRow = [
  CompanyCellModel,
  CreatedCellModel,
  ConnectionsCellModel,
  ConnectionsCellModel,
  ConnectionsCellModel,
  ConnectionsCellModel,
  CTACellModel
];

export function getColumnDefinitions(): ColumnDefinition<
  "name" | "created" | "lastSync" | "connections" | "cta"
>[] {
  return [
    {
      key: "name",
      label: "Company",
      component: CompanyCell,
      className: styles.companyCell,
      sortable: true,
    },
    {
      key: "created",
      label: "Created",
      component: CreatedCell,
      className: styles.genericCell,
      sortable: true,
    },
    {
      key: "lastSync",
      label: "Last pulled",
      component: LastPulledCell,
      className: styles.genericCell,
      sortable: true,
    },
    {
      key: "connections",
      label: "Connections",
      component: ConnectionsCell,
      className: styles.genericCell,
      colWidth: 30,
    },
    {
      key: "cta",
      label: "",
      component: CtaCell,
      className: styles.genericCell,
      align: "right",
    },
  ];
}

const MAX_CONNECTIONS_TO_SHOW = 2;

interface CompaniesTableProps {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  companies?: GetCompaniesResponse;
  isSkeleton: boolean;
  onRequestData: (company: GetCompanyResponse) => void;
  onDeleteButtonClick: (company: { id: string; name: string }) => void;
  onRowClick?: (index: number) => void;
  onEditButtonClick: (company: EditCompanyFormState) => void;
  searchQuery?: string;
  onColumnSort: (column: CompaniesSortKey) => void;
  sortDirection: SortDirection | undefined;
  sortKey: CompaniesSortKey | undefined;
  onZeroStateCtaClick: () => void;
  onClearSearch: () => void;
  onClearFilters: () => void;
  hasDataConnectionStatusFilter: boolean;
}

function getEmptyStateProps({
  searchQuery,
  onZeroStateCtaClick,
  onClearFilters,
  hasDataConnectionStatusFilter,
  onClearSearch,
}: Pick<
  CompaniesTableProps,
  | "searchQuery"
  | "onZeroStateCtaClick"
  | "onClearFilters"
  | "onClearSearch"
  | "hasDataConnectionStatusFilter"
>): Pick<
  ZeroStateProps,
  "icon" | "title" | "description" | "ctaText" | "onCtaClick"
> {
  if (!searchQuery && !hasDataConnectionStatusFilter) {
    return {
      icon: <BuildingIcon />,
      title: "No companies exist",
      description:
        "You don't have any companies created. A company represents your business customer that shares their data with you. Create a new company to start accessing that data.",
      ctaText: "Create company",
      onCtaClick: onZeroStateCtaClick,
    };
  }

  if (hasDataConnectionStatusFilter) {
    return {
      icon: <SearchIcon />,
      title: "No companies found",
      description:
        "No companies match your selected criteria. Try removing some filters.",
      ctaText: "Clear filters",
      onCtaClick: onClearFilters,
    };
  }

  return {
    icon: <SearchIcon />,
    title: "Try another search",
    description: `No results found for "${searchQuery}"`,
    ctaText: "Clear search",
    onCtaClick: onClearSearch,
  };
}

const CompaniesTable: React.FC<CompaniesTableProps> = (props) => {
  const {
    onRequestData,
    page,
    setPage,
    pageSize,
    setPageSize,
    companies,
    isSkeleton,
    onDeleteButtonClick,
    onEditButtonClick,
    searchQuery,
    onColumnSort,
    sortDirection,
    sortKey,
    onZeroStateCtaClick,
    onClearSearch,
    hasDataConnectionStatusFilter,
    onClearFilters,
  } = props;

  const history = useHistory();
  const { userState } = useUserState();

  const [buttonVariant, setButtonVariant] = useState<number | undefined>();

  const userCanUpdate = useMemo(
    () =>
      userState?.userPermissions?.includes("clients_companies_edit") ?? false,
    [userState?.userPermissions]
  );

  const tableRows = useMemo(() => {
    const userCanViewCompanyData = [
      "data_all_view",
      "data_bankAccounts_view",
      "data_bankStatements_view",
      "data_bills_view",
      "data_billCreditNotes_view",
      "data_creditNotes_view",
      "data_customers_view",
      "data_financials_view",
      "data_info_view",
      "data_invoices_view",
      "data_journals_view",
      "data_payments_view",
      "data_suppliers_view",
    ].some((p) => userState?.userPermissions?.includes(p));

    const userCanDeleteCompany =
      userState?.userPermissions?.includes("clients_companies_delete") ?? false;

    return (
      companies?.results?.map((row, index) => {
        const onRowClick = userCanViewCompanyData
          ? (e?: React.MouseEvent<HTMLTableRowElement>) => {
              if (e?.ctrlKey || e?.metaKey) {
                window.open(
                  `${window.location.origin}${formatUrl(
                    paths.companies.SUMMARY,
                    {
                      companyId: row.id,
                    }
                  )}`,
                  "_blank"
                );
              } else {
                history.push(
                  formatUrl(paths.companies.SUMMARY, { companyId: row.id })
                );
              }
            }
          : undefined;

        return {
          data: [
            {
              key: "name",
              value: {
                name: row.name,
                guid: row.id,
                userCanUpdate,
                onEditButtonClick: () =>
                  onEditButtonClick({
                    id: row.id,
                    name: row.name,
                    description: row.description,
                    tags: row.tags,
                    products: row.products,
                  }),
                searchQuery,
              },
            },
            {
              key: "created",
              value: {
                createdByUser: row.createdByUserName || "-",
                createdDate: row.created
                  ? formatDateAndTimeWithShortString(row.created, true)
                  : "-",
                searchQuery,
              },
            },
            {
              key: "lastSync",
              value: row.lastSync
                ? formatDateAndTimeWithShortString(row.lastSync, true)
                : "",
            },
            {
              key: "connections",
              value: {
                companyId: row.id,
                onPlatformClick: () => {
                  onRequestData(row);
                },
                maximumShownConnections: MAX_CONNECTIONS_TO_SHOW,
                connections: row.dataConnections,
              },
            },
            {
              key: "cta",
              value: {
                guid: row.id,
                numOfConnections: row.dataConnections.length,
                onRequestClick: () => onRequestData(row),
                onDeleteButtonClick: () => onDeleteButtonClick({ ...row }),
                onRowClick: onRowClick,
                showDelete: userCanDeleteCompany,
                buttonVariant: buttonVariant,
                index: index,
              },
            },
          ] as unknown as CompanyTableRow,
          onClick: onRowClick,
          onMouseEnter: onRowClick ? () => setButtonVariant(index) : undefined,
          onMouseLeave: onRowClick
            ? () => setButtonVariant(undefined)
            : undefined,
        };
      }) || []
    );
  }, [
    userState?.userPermissions,
    companies?.results,
    userCanUpdate,
    searchQuery,
    buttonVariant,
    history,
    onEditButtonClick,
    onRequestData,
    onDeleteButtonClick,
  ]);

  if (!isSkeleton && companies && companies.totalResults === 0) {
    return (
      <ZeroState
        className={styles.emptyState}
        data-testid="companies-table-empty-state"
        {...getEmptyStateProps({
          searchQuery,
          hasDataConnectionStatusFilter,
          onZeroStateCtaClick,
          onClearFilters,
          onClearSearch,
        })}
      />
    );
  }

  return (
    <>
      <DataTable
        className={styles.companiesTable}
        isSkeleton={isSkeleton}
        skeletonRows={pageSize * 1.6} // We have additional padding that means the skeleton rows don't match up with the actual sizes
        columnDefs={isSkeleton ? [] : getColumnDefinitions()}
        rowsInfo={tableRows}
        data-testid="companies-table"
        rowName="Company"
        buildEventProperties={(data) => {
          return { companyId: data[0].value.guid };
        }}
        onSort={onColumnSort}
        sortDirection={sortDirection}
        sortKey={sortKey}
        isLayoutAuto
        hasBorder
      />
      <Pagination
        label="Pagination"
        pageIndex={page}
        perPage={pageSize}
        perPageOptions={[10, 20, 50]}
        total={companies?.totalResults || 0}
        onUpdatePageIndex={setPage}
        onUpdatePerPage={setPageSize}
        isSkeleton={isSkeleton}
      />
    </>
  );
};

export default CompaniesTable;
